import { getProxyAppName, getProxyLink, getSiteUrl, normalizeItemSlug } from "@repo/shared"
import React from "react"

import { MarketsInfo } from "../../../servers/spotoverseascenter/interface"
import { NavigationItem } from "../../MainNavigation/type"
import { TranslateFunction } from "../types"

/**
 * 判断是否为测试环境
 */
export function isTest(): string {
  const env = process.env.NEXT_PUBLIC_ENV
  return env !== "production" ? "test" : ""
}

/**
 * 生成 Database 菜单项的标题（包含 Pro 标签）
 */
function createDatabaseTitle(t: TranslateFunction): React.ReactNode {
  return (
    <div key="database-pro-title">
      {t("menu.Database")}
      <sup
        style={{
          color: "#D7000F",
          fontSize: 8,
          marginLeft: 1,
        }}
      >
        {t("menu.DatabasePro")}
      </sup>
    </div>
  )
}

/**
 * 生成 Markets 菜单
 */
function generateMarketsMenu(markets: MarketsInfo[] | undefined, locale: string, t: TranslateFunction): NavigationItem {
  // 同域挂载的子应用不能用自身的客户端路由打开主站介绍页。
  const reloadDocument = Boolean(getProxyAppName())
  return {
    id: "markets",
    title: t("menu.Markets"),
    href: "#",
    mobileMenu: {
      mode: "drilldown",
      leadingLink: {
        title: t("utilityNav.MyWatchlist"),
        href: getSiteUrl("www") + `/${locale}/watchlist`,
      },
    },
    children:
      markets?.map((item) => {
        // A translated title cannot be used as the English category route key.
        const routeTopName = item.routeTopName || (locale === "en" ? item.top_name : "")
        const introductionHref =
          item.introduction_published && routeTopName
            ? getSiteUrl("www") + `/${locale}/categories/${normalizeItemSlug(routeTopName)}`
            : ""

        return {
          title: item.top_name,
          href: introductionHref,
          reloadDocument,
          isHot: item.is_hot,
          children: (item.next_list || []).map((child) => {
            return {
              title: child.second_name,
              href: getSiteUrl("www") + `/${locale}/${child.linked_item}`,
            }
          }),
        }
      }) || [],
  }
}

/**
 * 生成 DataCenter 菜单
 */
function generateDataCenterMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.DataCenter"),
    href: "#",
    mobileMenu: {
      mode: "drilldown",
    },
    children: [
      {
        title: t("menu.PriceCenter"),
        href: getSiteUrl("www") + getProxyLink({ locale, path: "", appName: "price-center" }),
      },
      {
        title: createDatabaseTitle(t),
        href: ``,
        children: [
          {
            title: t("menu.Web"),
            href: `https://${isTest()}data-pro.metal.com/`,
            openInNewTab: true,
          },
          {
            title: t("menu.ExcelAddIn"),
            href: getSiteUrl("www") + `/${locale}/excel-add-in`,
            openInNewTab: true,
          },
          {
            title: t("menu.API"),
            href: getSiteUrl("www") + `/${locale}/smm-api`,
            openInNewTab: true,
          },
          {
            title: t("menu.MCP"),
            href: getSiteUrl("www") + `/${locale}/mcp`,
            openInNewTab: true,
          },
        ],
      },
      {
        title: t("menu.PriceWidget"),
        href: getSiteUrl("www") + `/${locale}/price-widget`,
      },
      {
        title: t("menu.Tools"),
        href: getSiteUrl("www") + `/${locale}/tools`,
      },
    ],
  }
}

/**
 * 生成 NewsAndReports 菜单
 */
function generateNewsAndReportsMenu(locale: string, t: TranslateFunction): NavigationItem {
  const reportsBase = getSiteUrl("reports") + (locale === "en" ? "" : `/${locale}`)
  const children: NavigationItem[] = [
    {
      title: t("menu.News"),
      href: getSiteUrl("news") + `/${locale}`,
    },
    {
      title: t("menu.Reports"),
      href: reportsBase,
    },
    {
      title: t("menu.DailyPriceReports"),
      href: `${reportsBase}/price-daily`,
    },
  ]

  return {
    title: t("menu.NewsAndReports"),
    href: "#",
    children,
  }
}

/**
 * 生成 Events 菜单
 */
function generateEventsMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.Events"),
    href: "#",
    children: [
      {
        title: t("menu.EventsAndFieldTrip"),
        href: getSiteUrl("www") + `/${locale}/events`,
      },
      {
        title: t("menu.Webinars"),
        href: getSiteUrl("www") + getProxyLink({ locale, path: "", appName: "webinar" }),
      },
      {
        title: t("menu.SmmTier1"),
        href: getSiteUrl("www") + `/${locale}/smm-tier-1`,
      },
    ],
  }
}

/**
 * 生成 Methodology 菜单
 */
function generateMethodologyMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.Methodology"),
    href: "#",
    children: [
      {
        title: t("menu.Methodology"),
        href: getSiteUrl("www") + `/${locale}/methodology`,
      },
      {
        title: t("menu.HolidayPricingSchedule"),
        href: getSiteUrl("www") + `/${locale}/holiday-pricing-schedule`,
      },
      {
        title: t("menu.ComplianceCenter"),
        href: getSiteUrl("www") + `/${locale}/compliance-center/compliance-statement`,
      },
    ],
  }
}

/**
 * 生成菜单数据（主函数）
 */
export function generateMenuData(
  markets: MarketsInfo[] | undefined,
  locale: string,
  t: TranslateFunction
): NavigationItem[] {
  const industryLink = (path: string) =>
    getSiteUrl("www") +
    getProxyLink({
      locale,
      path,
      appName: "industry",
    })

  return [
    generateMarketsMenu(markets, locale, t),
    {
      title: t("menu.Industry"),
      href: "#",
      mobileMenu: {
        mode: "drilldown",
      },
      children: [
        {
          title: t("menu.Car"),
          href: getSiteUrl("car"),
          openInNewTab: true,
        },
        {
          title: t("menu.PreciousMetals"),
          href: industryLink("precious-metals"),
          pcHide: true,
          children: [
            {
              title: t("menu.PreciousHome"),
              href: industryLink("precious-metals/home"),
            },
            {
              title: t("menu.PreciousGold"),
              href: industryLink("precious-metals/gold"),
            },
            {
              title: t("menu.PreciousSilver"),
              href: industryLink("precious-metals/silver"),
            },
            {
              title: t("menu.PreciousPGMs"),
              href: industryLink("precious-metals/pgms"),
            },
          ],
        },
        {
          title: t("menu.AIInfrastructure"),
          href: industryLink("ai-infra"),
          pcHide: true,
          children: [
            {
              title: t("menu.AIList.Overview"),
              href: industryLink("ai-infra/overview"),
            },
            {
              title: t("menu.AIList.MetalPower"),
              href: industryLink("ai-infra/metals-power"),
            },
            {
              title: t("menu.AIList.ComputePrices"),
              href: industryLink("ai-infra/compute-prices"),
            },
            {
              title: t("menu.AIList.Insights"),
              href: industryLink("ai-infra/insights"),
            },
          ],
        },
      ],
    },
    generateDataCenterMenu(locale, t),
    generateNewsAndReportsMenu(locale, t),
    generateEventsMenu(locale, t),
    generateMethodologyMenu(locale, t),
  ]
}
